import { type BasketChange } from '~/context/basket-context';
import { type components } from '~/fecom-api/schema';

export type GtmItemListId =
  | 'related-products'
  | 'pdp'
  | 'plp'
  | `plp-${string}`
  | 'lp'
  | `lp-${string}`
  | 'quick-view'
  | 'basket'
  | 'basket-quick-add'
  | `picker`
  | 'recommendations'
  | `recommendations-${string}`
  | 'quick-order'
  | 'search'
  | undefined;

export type GtmEvent =
  | GtmBaseEvent
  | GtmAddToCartEvent
  | GtmAddToWishlistEvent
  | GtmRemoveFromCartEvent
  | GtmViewItemEvent
  | GtmViewItemListEvent
  | GtmViewCartEvent
  | GtmLoginEvent
  | GtmSelectItemEvent
  | GtmSearchEvent
  | GtmClearEcommerceEvent;

export type GtmBaseEvent = {
  event: 'base_event';
  logged_in: boolean;
  user_id?: string;
  ip_address?: string;
  country_code?: string;
  account_no?: string;
  email_address?: string; // ? Should this be here?
  price_group?: string;
};

type GtmClearEcommerceEvent = {
  event: 'clear_ecommerce';
  ecommerce: null;
};

type GtmItem = {
  // sku
  /**
   * The ID of the item.
   *
   * \*One of item_id or item_name is required.
   */
  item_id: string;
  item_ref: string; // Custom field for catalogue ref
  /**
   * The name of the item.
   *
   * \*One of item_id or item_name is required.
   */
  item_name?: string;
  /**
   * A product affiliation to designate a supplying company or brick and mortar store location.
   * Note: `affiliation` is only available at the item-scope.
   */
  affiliation?: string;
  /**
   * The coupon name/code associated with the item.
   *
   * Event-level and item-level coupon parameters are independent.
   */
  coupon?: string;
  /**
   * The unit monetary discount value associated with the item.
   */
  discount?: number;
  index?: number;
  /**
   * The brand of the item.
   */
  item_brand?: string;
  item_category?: string;
  item_category2?: string;
  item_category3?: string;
  item_category4?: string;
  item_category5?: string;
  // e.g. 'PLP', 'PDP', 'related_products'
  /**
   * The ID of the list in which the item was presented to the user.
   * If set, event-level item_list_id is ignored.
   * If not set, event-level item_list_id is used, if present.
   */
  item_list_id?: GtmItemListId;
  // e.g. 'PLP', 'PDP', 'related_products'
  item_list_name?: string;
  /**
   * The item variant or unique code or description for additional item details/options.
   */
  item_variant?: string;
  /**
   * The physical location associated with the item (e.g. the physical store location).
   * It's recommended to use the Google Place ID that corresponds to the associated item. A custom location ID can also be used.
   * Note?: `location id` is only available at the item-scope.
   */
  location_id?: string; // ?
  /**
   * The price of the item. Inc VAT - which is wrong but consistent with current fecom
   */
  price?: number;
  price_inc_vat?: number;
  price_exc_vat?: number;
  quantity?: number;
};

export type GtmAddToCartEvent = {
  event: 'add_to_cart';
  ecommerce: {
    currency?: string;
    value?: number;
    valueExcVat?: number; // should this still be this way around?
    items: GtmItem[];
  };
};

export type GtmAddToWishlistEvent = {
  event: 'add_to_wishlist';
  ecommerce: {
    items: GtmItem[];
  };
};

export type GtmRemoveFromCartEvent = {
  event: 'remove_from_cart';
  ecommerce: {
    items: GtmItem[];
  };
};

export type GtmViewCartEvent = {
  event: 'view_cart';
  ecommerce: {
    currency?: string;
    value?: number;
    valueExcVat?: number; // should this still be this way around?
    //
    discount?: number;
    shipping?: number;
    coupon?: string;
    //
    items: GtmItem[];
  };
};

export type GtmViewItemEvent = {
  event: 'view_item';
  ecommerce: {
    currency?: string;
    value?: number;
    valueExcVat?: number; // should this still be this way around?
    items: GtmItem[];
  };
};
export type GtmViewItemListEvent = {
  event: 'view_item_list';
  ecommerce: {
    item_list_id?: GtmItemListId;
    item_list_name: string;
    currency?: string;
    items: GtmItem[];
  };
};

export type GtmLoginEvent = {
  event: 'login';
  method: string;
};

export type GtmSelectItemEvent = {
  event: 'select_item';
  ecommerce: {
    item_list_id: string;
    item_list_name: string;
    items: GtmItem[];
  };
};

export type GtmSearchEvent = {
  event: 'search';
  search_term: string;
};

type MapProductToGtmItemParams = {
  product: components['schemas']['Product'];
  quantity: number;
  overrides?: Partial<GtmItem>;
};

export const mapProductToGtmItem = ({
  product,
  quantity,
  overrides,
}: MapProductToGtmItemParams): GtmItem => {
  return {
    item_id: product.itemCode,
    item_ref: product.catalogueRef,
    item_name: product.productName,
    item_brand: product?.brandName,
    discount: product.wasPriceExcVat
      ? product.priceExcVat - product.wasPriceExcVat
      : undefined,
    quantity: quantity,
    price: product.priceIncVat, // TODO - check if this is correct
    price_inc_vat: product.priceIncVat, // TODO - check if this is correct
    price_exc_vat: product.priceExcVat, // TODO - check if this is correct
    ...overrides,
  };
};

export const gtmDataLayerEvent = (data: GtmEvent) => {
  if (typeof window === 'undefined') {
    console.error(null, 'Failed to trigger GTM for event ' + data.event);
    throw new Error('Failed to trigger GTM for event ');
  }

  window.dataLayer = window.dataLayer || [];

  // clear ecommerce data for certain events
  // this is to prevent duplicate data being sent to GTM
  // when using the ecommerce object
  switch (data.event) {
    case 'base_event': {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { event, ...newData } = data;
      window.dataLayer.push({
        ecommerce: null,
      });
      window.dataLayer.push(newData);
      return;
    }
    default:
      window.dataLayer.push({
        ecommerce: null,
      });
      break;
  }

  // define dataLayer so we can still queue up events before GTM init
  window.dataLayer.push(data);
};

export const gtmAddProductsToCart = ({
  products,
  itemListId,
  itemListName,
  currency,
}: {
  products: [
    components['schemas']['Product'] | components['schemas']['ProductDetails'],
    number | undefined,
  ][];
  itemListId?: GtmItemListId;
  itemListName?: string;
  currency: string;
}): void => {
  const value = products.reduce((acc, [product, quantity]) => {
    if (quantity) {
      return acc + (product.priceIncVat ?? 0) * quantity;
    }
    return acc;
  }, 0);

  const valueExcVat = products.reduce((acc, [product, quantity]) => {
    if (quantity) {
      return acc + (product.priceExcVat ?? 0) * quantity;
    }
    return acc;
  }, 0);
  gtmDataLayerEvent({
    event: 'clear_ecommerce',
    ecommerce: null,
  });
  gtmDataLayerEvent({
    event: 'add_to_cart',
    ecommerce: {
      currency,
      value,
      valueExcVat,
      items: products.map(([product, quantity]) =>
        mapProductToGtmItem({
          product,
          quantity: quantity ?? 1,
          overrides: {
            item_list_id: itemListId,
            item_list_name: itemListName,
          },
        }),
      ),
    },
  });
};

export const gtmBasketChanges = ({
  rows,
  changes,
  products,
}: {
  rows: components['schemas']['BasketRow'][];
  products: components['schemas']['ProductDetails'][];
  changes: BasketChange[];
}) => {
  const addedItems: GtmItem[] = [];
  const removedItems: GtmItem[] = [];

  // for each change get the product from the products and the quantity from the rows
  changes.forEach((change) => {
    // need to be careful here as itemcode is actually cat ref
    const product = products.find(
      (product) => product.catalogueRef === change.itemCode,
    );
    const row = rows.find((row) => row.itemCode === change.itemCode);
    if (!product || !row) {
      return;
    }

    const initialQuantity = row?.quantity ?? 0;
    const newQuantity = change.quantity ?? 0;

    const quantityDifference = newQuantity - initialQuantity;
    const isAdd = Math.sign(quantityDifference) === 1;

    const item = mapProductToGtmItem({
      product,
      quantity: Math.abs(quantityDifference),
      overrides: {
        item_list_id: 'basket',
        item_list_name: 'Basket',
      },
    });

    if (isAdd) {
      addedItems.push(item);
    } else {
      removedItems.push(item);
    }
  }) ?? [];

  removedItems.length > 0 &&
    gtmDataLayerEvent({
      event: 'remove_from_cart',
      ecommerce: {
        items: removedItems,
      },
    });
  addedItems.length > 0 &&
    gtmDataLayerEvent({
      event: 'add_to_cart',
      ecommerce: {
        items: addedItems,
      },
    });
};
